/**
 * useColumnWidths — drag-to-resize width overrides for a table's columns,
 * persisted to localStorage so a recruiter's choice survives reloads.
 * Best-effort persistence: any storage failure (private mode, SSR, tests) falls
 * back to the in-memory default without throwing (mirrors `useSidebarCollapse.ts`).
 *
 * **An override is stored only once the user drags.** `widthFor` answers
 * `undefined` for a column nobody has resized, and the caller then renders NO
 * width style at all — the column keeps whatever `table-layout: auto` gives it.
 * That is what lets checklist 7 extend resize to the static columns without
 * also redesigning the table's proportions: pinning a default width on Name /
 * the write-outcome column / Thrive ID would have changed how every list looks
 * for everyone, and resize was the ask, not a re-layout. Two columns are the exception and
 * supply a fallback at the call site, because they were built pinned: the
 * annex columns, which now supply a MEASURED width capped at
 * `DEFAULT_ANNEX_WIDTH` rather than the bare constant they used to pass, and
 * Contact info, which still supplies the flat `CONTACT_INFO_WIDTH` on purpose
 * — "wider by default" is what that column was asked for, and content-fitting
 * it would answer that request by narrowing it.
 *
 * **Keys are namespaced `<tableId>:<columnKey>`.** The map used to be keyed by
 * the bare column key, which was safe only while exactly one table used the
 * hook: two tables with a `name` column would have shared one width. Widths
 * stay global across imports on purpose — the AI-Review criteria (developing /
 * growth / Marketing / ...) are stable and reused, so "size Marketing once, it
 * sticks everywhere". Scope per-import by passing the importId in `tableId`.
 *
 * Writes MERGE against whatever storage holds at write time, so two mounted
 * tables do not clobber each other — see the note at the write. Namespacing
 * the keys alone did NOT buy that, though an earlier version of this docstring
 * said it did.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "nu-assist:web:contacts-column-widths";

/**
 * Annex column width CEILING in px (22rem).
 *
 * It was the annex columns' flat default — "preserves the pre-resize look" —
 * and that is exactly what was wrong with it: every criterion got 352px
 * whatever it held, so one whose rationales are all "Yes" reserved the same
 * space as one holding two sentences. It was never wrong as a ceiling; it was
 * wrong as a floor and a ceiling at once.
 *
 * `ContactsTable` now pins each annex column at
 * `clamp(max-content, MIN_WIDTH, DEFAULT_ANNEX_WIDTH)` via `fitWidth` in
 * `measureColumnWidths.ts`, and falls back to this constant when there is no
 * measurement to be had (first render, or a DOM with no layout engine) — so
 * the un-measured case is byte-for-byte the old behaviour.
 */
export const DEFAULT_ANNEX_WIDTH = 352;
/**
 * Default width of the contacts table's "Contact info" column, in px (20rem).
 *
 * This is cl.4 item 8's "wider by default", and — measured, not assumed — it
 * is THE fix for the shredded address, not half of one. `table-layout: auto`
 * sizes a column from its content, and an email's min-content collapses to one
 * character under any of the wrap rules available (`break-all` and
 * `overflow-wrap: anywhere` measured identical: column 111px, address on 3
 * lines, row 326px tall). Pinning the column means its width is never derived
 * from content, so the collapse cannot happen. Measured pinned: same address,
 * ONE line, row 127px.
 *
 * The other thing that had to change was inside `EnrichData` — the label and
 * provenance badge moved off the address's line, because they were taking
 * 145px of a 264px cell and the width was never reaching the text.
 *
 * It lives here rather than in `ContactsTable` so the component module keeps
 * exporting only components (React Fast Refresh) and so every column-width
 * default sits in one file.
 */
export const CONTACT_INFO_WIDTH = 320;
/**
 * Lower clamp in px (8rem). MUST stay at/above a cell's real min-content
 * (score badge + one wrapped word) — below it, `table-layout: auto` expands the
 * column past the set width and the width stops being authoritative.
 */
export const MIN_WIDTH = 128;
/**
 * THERE IS NO UPPER CLAMP. A column can be dragged as wide as the reader wants
 * and the wrapper's horizontal scroll absorbs it.
 *
 * There was one — 640px — removed deliberately, and the reason it was safe to
 * remove is the same reason it looked harmless to have: it never protected
 * anything. A column wider than the viewport costs a horizontal scroll, which
 * this table already has and already needs on any Company Map list. What the
 * ceiling did instead was silently refuse the last part of a drag, which reads
 * as the handle sticking.
 *
 * `End` went with it — its only meaning was "jump to the ceiling" — and does
 * not come back. `aria-valuemax` DID come back: omitting it does not open the
 * range, ARIA defaults it to 100, which announced a maximum below the minimum.
 * See `ARIA_MAX_WIDTH` in `ContactsTable`, which is an announcement bound and
 * not a clamp.
 */
/** Keyboard resize increment in px (ArrowLeft/ArrowRight). */
export const STEP = 16;

/** Clamp to `[MIN_WIDTH, ∞)`. `min` is overridable for a caller that needs a
 *  taller floor; nothing overrides it today, and `setWidth` no longer forwards
 *  a parameter no call site ever passed (`/pr-review` N3).
 *
 *  The floor stays and is load-bearing in a way the old ceiling never was: below
 *  a cell's real min-content, `table-layout: auto` expands the column past the
 *  set width and the width stops being authoritative at all. */
export function clampWidth(px: number, min: number = MIN_WIDTH): number {
  return Math.max(min, Math.round(px));
}

/** The subset of a persisted map belonging to one table. */
function ownedBy(all: Record<string, number>, tableId: string): Record<string, number> {
  const prefix = `${tableId}:`;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(all)) {
    if (key.startsWith(prefix)) out[key] = value;
  }
  return out;
}

/**
 * Read the persisted map, adopting any pre-namespace entries.
 *
 * The unnamespaced form was written by exactly one table (the import-detail
 * contacts table, annex columns only), so `contacts:` is the right owner for
 * every legacy key. Adopting rather than dropping means nobody silently loses a
 * width they set; the legacy entry is left in place because the next write
 * rewrites the whole blob anyway.
 */
function readPersisted(): Record<string, number> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    // Keep only finite numbers; drop anything else a stale/hand-edited blob holds.
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      // CLAMPED ON READ, not just on write. `setWidth` is the only writer and it
      // clamps, so nothing reachable today can store an out-of-range width — but
      // `movesWrongWay`'s whole argument rests on "everything persisted is
      // clamped", and until this line that was true by CONVENTION rather than by
      // construction. A hand-edited blob, or any future change to MIN_WIDTH,
      // otherwise paints a `<th>` at a width no gesture can produce and reports
      // it as `aria-valuenow` below its own `aria-valuemin` (`/pr-review`
      // iter 9, Agent A N4 + Agent C C14). Only the FLOOR is checkable now —
      // there is no ceiling to be outside of.
      // "Contains a colon" is the has-a-namespace test, and it is deliberately
      // WIDE. A narrower `key.startsWith("contacts:")` was tried (`/pr-review`
      // iters 1 + 7, Agent A N5) and is WRONG: it stops recognising ANOTHER
      // table's key as namespaced, so `other:foo` gets re-prefixed to
      // `contacts:other:foo` and that table's width is destroyed. The two
      // multi-table tests below catch it — they are the ones iter 2 added.
      //
      // The known cost of the wide test, unfixed: annex keys are free text
      // (`build_annex_columns` sets `key = label = criterion_name`), so a
      // PRE-NAMESPACE width saved under a criterion containing a colon — say
      // "Fit: strategic" — reads as already-namespaced and is orphaned rather
      // than adopted. One migration window, one table, and the user loses a
      // column width they set. Fixing it properly means matching a table-id
      // SHAPE rather than a bare colon, which is a guess about what a table id
      // may look like; not worth making inside this card.
      out[key.includes(":") ? key : `contacts:${key}`] = clampWidth(value);
    }
    return out;
  } catch {
    return {};
  }
}

/** What a resize gesture MEANS. A union of words, not a number, on purpose —
 *  see `movesWrongWay`. */
export type ResizeIntent = "narrower" | "wider";

export interface WidthGesture {
  /** The column's width when the gesture began.
   *
   *  MEASURED — and therefore possibly BELOW `MIN_WIDTH` — for a
   *  column with no override, which is precisely the case the rule exists for
   *  and precisely why this module cannot compute it: `widthFor(key)` is
   *  `undefined` for exactly those columns, and there is no DOM at this
   *  boundary. The caller has it; the hook never can. */
  from: number;
  /** What the gesture means. NEVER derive this from `target - from`. */
  intent: ResizeIntent;
}

/**
 * **THE resize rule: a gesture may never move a column in the direction
 * OPPOSITE to what the gesture means.**
 *
 * Asked by every path that commits a width — and by `setWidth` itself, which is
 * what makes it a rule rather than a convention. It takes the TARGET — the
 * value that would actually be written — rather than a delta, and that is the
 * whole point: an earlier version tested clamp saturation instead
 * (`current <= MIN_WIDTH`, and a ceiling test that no longer exists), which is
 * a DIFFERENT
 * statement that merely coincides with this one for the arrows and the pointer.
 * It came apart on the two destination keys, and the tell was that `Home` then
 * needed a hand-written exemption while `End` did not — the exemption was the
 * symptom, not the oversight (`/pr-review` iter 9, Agent C C12).
 *
 * **Why it is exported although the hook enforces it.** The component asks the
 * rule what to PAINT; the hook asks the same rule what to STORE. Two decisions,
 * one predicate. `ContactsTable`'s `moveResize` consults it before writing a
 * preview frame, which is not a persistence call at all — dropping that use
 * re-opens the 1px-jitter-paints-a-410px-snap defect (iter 9, C13).
 *
 * The mechanism it guards: an UNRESIZED column's start width is MEASURED, so it
 * can sit BELOW `MIN_WIDTH`, while everything committed is clamped up into
 * range. The `export` column's cells are empty for `null`/`pending` (FR7) so it
 * can measure ~70. Every wrong-way move that follows persists GLOBALLY, since
 * widths are shared across imports by design.
 *
 * HALF THIS RULE'S SURFACE WENT AWAY WITH THE CEILING. It used to guard two
 * boundaries; a measured 1050 narrowing to a saturated 640 was the upper one,
 * and with no ceiling a narrowing gesture now lands wherever the drag says.
 * Only the floor can still invert an intent, and only for a column measuring
 * under 128 — which is why the rule is kept rather than deleted: `export` is
 * such a column today, and the predicate is what stops `ArrowLeft` on it
 * WIDENING it to 128.
 *
 * What it deliberately allows, because this is an honest LANDING rather than a
 * wrong-way move: widening from 70 lands on 128, the narrowest legal
 * override. Its MAGNITUDE is a
 * separate problem with a separate guard; see `ADOPTION_TOLERANCE_PX` in
 * `ContactsTable`.
 *
 * Four surfaces had this defect, one per review iteration — the still click,
 * the keyboard arrows, the pointer, and `Home` — because each was fixed as a
 * case instead of as an invariant. For the paths whose target is DERIVED,
 * `target = clamp(current + delta)`: for `current > MIN`,
 * `delta > 0 ⇒ target >= current` and `delta < 0 ⇒ target <= current`, so no
 * interior start can invert and every inversion lives at a boundary.
 *
 * **That argument did not cover `Home` and `End`, which is exactly why they
 * were the surface that broke while the derived paths held.** They NAME a
 * target instead of deriving one, so the premise does not apply. What protects
 * them is that `intent` is DECLARED from the key's meaning rather than computed
 * as `MIN_WIDTH - current` (and, while it existed, its `End` twin) — a
 * derived intent always
 * agrees in sign with `target - current`, which makes this function vacuous for
 * both keys and silently re-opens iter 7. A test caught that; see `keyResize`.
 *
 * **`intent` is a union of WORDS for that reason.** A numeric intent leaves the
 * derivation writable and type-clean, and `-1 | 0 | 1` does not fix it either —
 * `Math.sign(MIN_WIDTH - current)` is one cast away. You have to type the word,
 * which makes the historical bug unwritable. It also deletes a dead branch: the
 * old `intent === 0 ⇒ false` case was unreachable, since both pointer sites
 * short-circuit on their tolerance before the predicate is called.
 */
export function movesWrongWay(current: number, target: number, intent: ResizeIntent): boolean {
  return intent === "narrower" ? target > current : target < current;
}

export interface ColumnWidths {
  /** Stored override for a column, or `undefined` if nobody has resized it. */
  widthFor: (key: string) => number | undefined;
  /** Persist a width for a column, floored at `MIN_WIDTH` and unbounded above — and
   *  REFUSED outright if it would move the column against `gesture.intent`.
   *  The gesture is a required positional parameter: making it optional would
   *  reproduce today's opt-in state behind a stricter-looking signature. */
  setWidth: (key: string, px: number, gesture: WidthGesture) => void;
  /** Drop a column's override so it falls back to the table's own sizing.
   *
   *  Deliberately takes NO gesture. This is a deletion, not a move: there is no
   *  target for `movesWrongWay` to judge, and after it the width is whatever the
   *  browser computes — for Name, ~1050, out of range on purpose. A direction
   *  check here could REFUSE the reset (640 back to a measured 1050 is a
   *  widening), and reset is the one recovery path four docstrings lean on and
   *  the only one proved in a browser. */
  resetWidth: (key: string) => void;
}

export function useColumnWidths(tableId: string): ColumnWidths {
  // Seeded with THIS table's keys only. Seeding from the whole blob was the
  // other half of the last-writer-wins bug, and the half that survived the
  // first fix: a foreign key present at mount rode along in `widths` and got
  // re-written at its mount-time value by the merge below, so "everything else
  // is preserved" held only for keys that did not exist yet. `/pr-review` iter
  // 2 found it, and found that the test written for the merge could not see it
  // — `beforeEach` clears the store, so both hooks mounted on `{}` and there
  // was never a foreign key to clobber.
  const [widths, setWidths] = useState<Record<string, number>>(() =>
    ownedBy(readPersisted(), tableId)
  );

  // MERGE on write, never replace. Each hook instance holds its own snapshot,
  // taken at mount; writing that snapshot back would silently drop everything
  // another mounted table committed since. Namespacing the KEYS stopped two
  // tables colliding on `name`; it did nothing about owning the blob, and the
  // docstring above claimed otherwise until `/pr-review` said so.
  //
  // Re-reading storage at write time is the other half: this hook's own entries
  // win (they are the newer intent), everything else is preserved.
  useEffect(() => {
    try {
      const prefix = `${tableId}:`;
      const merged = { ...readPersisted() };
      for (const key of Object.keys(merged)) {
        if (key.startsWith(prefix)) delete merged[key];
      }
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify({ ...merged, ...widths }));
    } catch {
      // persistence is best-effort; ignore storage failures.
    }
  }, [widths, tableId]);

  const widthFor = useCallback(
    (key: string) => widths[`${tableId}:${key}`],
    [widths, tableId]
  );

  const setWidth = useCallback(
    (key: string, px: number, gesture: WidthGesture) => {
      // CLAMP FIRST, then judge. The rule is a statement about the value that
      // actually gets written, which is the clamped one — the same reason it
      // takes a target rather than a delta (`/pr-review` iter 9, C12).
      const target = clampWidth(px);
      if (movesWrongWay(gesture.from, target, gesture.intent)) {
        // Fail SOFT, and loudly. This is a cosmetic feature; a refused width is
        // a better outcome than a crash, and the repo's precedent for "the
        // caller is wrong but the user should not pay" is `console.warn`.
        //
        // Not gated on DEV: a future preset-menu bug should be visible in
        // production too. It can never fire from a normal user action, because
        // both existing callers ask the same rule first — see below.
        console.warn(
          `[useColumnWidths] refused a "${gesture.intent}" write for "${key}": ` +
            `${gesture.from}px -> ${target}px moves the column the other way.`
        );
        return;
      }
      setWidths((w) => ({ ...w, [`${tableId}:${key}`]: target }));
    },
    [tableId]
  );

  const resetWidth = useCallback(
    (key: string) => {
      setWidths(({ [`${tableId}:${key}`]: _dropped, ...rest }) => rest);
    },
    [tableId]
  );

  return { widthFor, setWidth, resetWidth };
}
